import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  createEducationFormStepper,
  educationFormStepper,
} from '@pages/hubs/Education/const/education-form-stepper';
import { EducationStepsEnum } from '@pages/hubs/Education/enum/education-steps-enum';
import { SelectEducationFormatEnum } from '@pages/hubs/Education/enum/select-education-format-enum';
import {
  acceptedEducationWS,
  addEducationWS,
  getEducations,
  updateEducationWS,
} from '@pages/hubs/Education/store/education-thunk';
import { UserConfirmStatusEnum } from '@shared/enums/hubs/user-confirm-status-enum';
import { isListOver } from '@shared/functions/isListOver';
import { changeCoverPhoto } from '@shared/utils/hubs/changeCoverPhoto';

import type { EducationItemsDataStep, EducationType } from '@pages/hubs/Education/store/education-types';
import type { RootMeta } from '@pages/hubs/Garage/store/types';
import type { GetHubsParams } from '@pages/hubs/Pets/store/pets-types';
import type { AssignPeoplePermissionsEnum } from '@shared/enums/assignPeoplePermissions.enum';
import type { AssignPeopleSelectValueModel } from '@shared/models/assignPeopleSelectValue.model';
import type { FilterParams } from '@shared/models/hubs/hub-filters.model';
import type { FormStepper } from '@shared/models/hubs/hub-form-stepper.model';
import type { MediaType } from '@shared/models/media.model';

const educationInitialState = {
  isLoadingEducation: false,
  isListOver: false as boolean | number,
  isEmptyEducationsDataWithFilters: false,
  currentEducation: {} as EducationType,

  educationEditMode: false,
  education: {
    data: [] as EducationType[],
    meta: {} as RootMeta,
  },
  share: {
    sharePeopleList: [] as AssignPeopleSelectValueModel[],
    selectUser: null as AssignPeopleSelectValueModel | null,
  },

  filterParams: {
    query: '',
    shared_filter: [],
  } as FilterParams,
  createEducation: {
    formStepper: educationFormStepper as FormStepper<EducationStepsEnum>,
    currentStep: EducationStepsEnum.preStepEducationType,
    educationType: SelectEducationFormatEnum.education,
    draftItem: null as null | EducationType,
    isCanChangeCurrentStep: true,
  },
};
type InitialStateType = typeof educationInitialState;
export const educationSlice = createSlice({
  name: 'educationSlice',
  initialState: educationInitialState as InitialStateType,
  reducers: {
    setLoadingEducation(state, action: PayloadAction<{ isLoading: boolean }>) {
      state.isLoadingEducation = action.payload.isLoading;
    },
    clearEducationFilters(state) {
      state.filterParams = { ...state.filterParams, query: '' };
    },

    // ------------------ main page ------------------
    setEducationFilterParams(state, action: PayloadAction<{ params: FilterParams }>) {
      state.filterParams = action.payload.params;
    },
    removeCurrentEducation(state, action: PayloadAction<{ educationID: number }>) {
      const index = state.education.data.findIndex(
        (education) => education.id === action.payload.educationID,
      );
      if (index !== -1) state.education.data.splice(index, 1);

      if (
        !state.education.data.length &&
        (!!state.filterParams.shared_filter.length || !!state.filterParams.query)
      ) {
        state.isEmptyEducationsDataWithFilters = true;
      }
    },
    setEducationDataWithNewPosition(state, action: PayloadAction<{ newPositionData: EducationType[] }>) {
      state.education.data = action.payload.newPositionData;
    },
    isEmptyDataEducationWithFilters(
      state,
      action: PayloadAction<{
        filters: Pick<GetHubsParams, 'shared_filter' | 'query'>;
        educationData: EducationType[];
      }>,
    ) {
      const { shared_filter, query } = action.payload.filters;
      state.isEmptyEducationsDataWithFilters =
        (!!shared_filter?.length || !!query) && !action.payload.educationData.length;
    },
    clearEducationState(state) {
      state.education = educationInitialState.education;
    },
    //  ------------------ view education ------------------
    setCurrentEducation(state, action: PayloadAction<{ education: EducationType }>) {
      state.currentEducation = action.payload.education;
    },
    // ------------------ gallery education ------------------

    addEducationPhoto(state, action: PayloadAction<{ newGalleryPhotos: MediaType[] }>) {
      state.currentEducation.photos = action.payload.newGalleryPhotos;
    },
    updateEducationCoverPhoto(state, action: PayloadAction<{ coverPhoto: MediaType }>) {
      state.currentEducation.cover_photo = action.payload.coverPhoto;
    },
    removeEducationCoverPhoto(
      state,
      action: PayloadAction<{ deleteCoverID: number; isCover: boolean | undefined }>,
    ) {
      const deleteCoverIndex = state.currentEducation.photos.findIndex(
        (photo) => photo.id === action.payload.deleteCoverID,
      );
      if (deleteCoverIndex !== -1) state.currentEducation.photos.splice(deleteCoverIndex, 1);
      if (!!state.currentEducation.photos.length) {
        const { photos } = changeCoverPhoto(state.currentEducation.photos);
        const hasCoverPhoto = state.currentEducation.photos.filter((photo) => photo.is_cover);

        state.currentEducation.photos = [...photos];
        if (!hasCoverPhoto.length) {
          state.currentEducation.cover_photo = photos.length ? photos[photos.length - 1] : null;
        }
      } else state.currentEducation.cover_photo = null;
    },
    // ------------------ files education ------------------
    addEducationFiles(state, action: PayloadAction<{ newDocuments: MediaType[] }>) {
      state.currentEducation.documents = action.payload.newDocuments;
    },
    setEducationEditMode(state, action: PayloadAction<{ editMode: boolean }>) {
      state.educationEditMode = action.payload.editMode;
    },
    // ------------------ share education ------------------
    setAssignPeopleLists(state, action: PayloadAction<{ peopleLists: AssignPeopleSelectValueModel[] }>) {
      state.share.sharePeopleList = action.payload.peopleLists;
    },
    setSelectedUser(state, action: PayloadAction<{ selectUser: AssignPeopleSelectValueModel | null }>) {
      state.share.selectUser = action.payload.selectUser;
    },
    addShareUser(state, action: PayloadAction<{ shareUser: AssignPeopleSelectValueModel }>) {
      state.currentEducation.users.push(action.payload.shareUser);
    },
    deleteEducationShareUser(state, action: PayloadAction<{ shareUserID: number }>) {
      const index = state.currentEducation.users.findIndex(
        (user: any) => user.id === action.payload.shareUserID,
      );
      if (index !== -1) state.currentEducation.users.splice(index, 1);
    },
    updateShareUser(
      state,
      action: PayloadAction<{ userRole: AssignPeoplePermissionsEnum; shareUserID: number }>,
    ) {
      const index = state.currentEducation.users.findIndex(
        (user: any) => user.id === action.payload.shareUserID,
      );
      if (index !== -1) state.currentEducation.users[index].role = action.payload.userRole;
    },
    // ------------------ create education ------------------
    setEducationCreateType: (state, action: PayloadAction<SelectEducationFormatEnum>) => {
      state.createEducation.educationType = action.payload;
    },

    setEducationCoverPhoto: (state, action: PayloadAction<{ cover_photo: MediaType | null }>) => {
      if (state.createEducation.draftItem) {
        state.createEducation.draftItem.cover_photo = action.payload.cover_photo;
      }
    },
    setEducationCreateDraftItem: (state, action: PayloadAction<any | null>) => {
      state.createEducation.draftItem = action.payload;
      if (action.payload) {
        state.createEducation.educationType = action.payload.type;
      }
    },
    updateEducationDraftItem: (state, action: PayloadAction<any | null>) => {
      state.createEducation.draftItem = { ...state.createEducation.draftItem, ...action.payload };
    },
    setEducationCreateCurrentStep: (state, action: PayloadAction<EducationStepsEnum>) => {
      state.createEducation.currentStep = action.payload;
      state.createEducation.isCanChangeCurrentStep = true;
    },
    changeEducationCreateFormStepperFromDraftItem: (state, action: PayloadAction<any[]>) => {
      const enabledSteps = new Set([EducationStepsEnum.preStepEducationType]);

      action.payload.forEach(({ step }) => {
        const currentStep = state.createEducation.formStepper[step as EducationStepsEnum];
        if (currentStep) {
          currentStep.isDisabled = enabledSteps.has(step);
          currentStep.isPassed = true;
        }
      });

      const lastStepInPayload = action.payload[action.payload.length - 1];

      if (lastStepInPayload) {
        const nextStep: EducationStepsEnum =
          createEducationFormStepper[
            createEducationFormStepper.findIndex((item) => item === lastStepInPayload.step) + 1
          ];
        const isStepCourses =
          state.createEducation.draftItem?.type === SelectEducationFormatEnum.courses &&
          nextStep === EducationStepsEnum.educationActivities;

        state.createEducation.currentStep = isStepCourses
          ? EducationStepsEnum.educationContact
          : (nextStep as EducationStepsEnum);
      }
    },
    setEducationCreateFormStepPassed: (
      state,
      action: PayloadAction<{ step: EducationStepsEnum; isPassed?: boolean }>,
    ) => {
      const { step, isPassed = true } = action.payload;
      if (state.createEducation.formStepper[step]) {
        state.createEducation.formStepper[step].isPassed = isPassed;
      }
    },
    setEducationCreateFormStepDisabled: (
      state,
      action: PayloadAction<{ step: EducationStepsEnum; isDisabled?: boolean }>,
    ) => {
      const { step, isDisabled = true } = action.payload;
      if (state.createEducation.formStepper[step]) {
        state.createEducation.formStepper[step].isDisabled = isDisabled;
      }
    },
    setEducationCreateFormStepSkip: (
      state,
      action: PayloadAction<{ step: EducationStepsEnum; isSkip?: boolean }>,
    ) => {
      const { step, isSkip = true } = action.payload;
      if (state.createEducation.formStepper[step]) {
        state.createEducation.formStepper[step].isSkip = isSkip;
      }
    },
    setEducationCreateCanChangeCurrentStep: (state, action: PayloadAction<boolean>) => {
      state.createEducation.isCanChangeCurrentStep = action.payload;
    },
    changeEducationCreateFormStepperPassedDisableSteps: (
      state,
      action: PayloadAction<{
        enabledSteps: EducationStepsEnum[];
        passedStep: EducationItemsDataStep[];
      }>,
    ) => {
      const enabledSteps = new Set(action.payload.enabledSteps);

      action.payload.passedStep.forEach(({ step }) => {
        const currentStep = state.createEducation.formStepper[step];
        if (currentStep) {
          currentStep.isDisabled = enabledSteps.has(step);
          currentStep.isPassed = true;
        }
      });
    },

    resetCreateStore: (state) => {
      state.createEducation = {
        formStepper: educationFormStepper,
        currentStep: EducationStepsEnum.preStepEducationType,
        educationType: SelectEducationFormatEnum.education,
        draftItem: null,
        isCanChangeCurrentStep: true,
      };
    },

    updateEducationCoverPhotoWS(
      state,
      action: PayloadAction<{ newCoverPhoto: MediaType; educationID: number }>,
    ) {
      const index = state.education.data.findIndex(
        (education) => education.id === action.payload.educationID,
      );

      if (index !== -1) state.education.data[index].cover_photo = action.payload.newCoverPhoto;
    },
  },
  extraReducers: ({ addCase }) => {
    addCase(getEducations.fulfilled, (state, { payload }) => {
      if (payload.meta.current_page === 1) {
        state.education.data = payload.data;
        state.education.meta = payload.meta;
        state.isListOver = isListOver(payload);
      } else {
        state.education.data.push(...payload.data);
        state.education.meta = payload.meta;
        state.isListOver = isListOver(payload);
      }
    });
    addCase(updateEducationWS.fulfilled, (state, action: PayloadAction<{ educationWS: EducationType }>) => {
      if (!!state.education.data.length) {
        const index = state.education.data.findIndex(
          (education) => education.id === action.payload.educationWS.id,
        );

        if (index !== -1) state.education.data[index] = action.payload.educationWS;
      }
    });
    addCase(addEducationWS.fulfilled, (state, action: PayloadAction<{ addEducationWS: EducationType }>) => {
      const index = state.education.data.findIndex(
        (education) => education.id === action.payload.addEducationWS.id,
      );

      if (index !== -1) {
        state.education.data[index] = action.payload.addEducationWS;
      } else {
        state.education.data = !!state.education.data.length
          ? [action.payload.addEducationWS, ...state.education.data]
          : [action.payload.addEducationWS];
      }
    });
    addCase(
      acceptedEducationWS.fulfilled,
      (state, action: PayloadAction<{ acceptedEducationWS: EducationType }>) => {
        const index = state.education.data.findIndex(
          (education) => education.id === action.payload.acceptedEducationWS.id,
        );
        state.education.data.splice(index, 1);

        const pending = state.education.data.filter(
          (education) => education.current.confirm_status === UserConfirmStatusEnum.pending,
        );
        const general = state.education.data.filter(
          (education) => education.current.confirm_status !== UserConfirmStatusEnum.pending,
        );

        state.education.data = [...pending, action.payload.acceptedEducationWS, ...general];
      },
    );
  },
});

export const {
  setLoadingEducation,
  changeEducationCreateFormStepperPassedDisableSteps,
  setEducationCreateCanChangeCurrentStep,
  setEducationCreateFormStepDisabled,
  changeEducationCreateFormStepperFromDraftItem,
  setEducationCreateDraftItem,
  setEducationCreateFormStepPassed,
  setEducationCreateFormStepSkip,
  setEducationCreateCurrentStep,
  setEducationCreateType,
  updateEducationDraftItem,
  setEducationCoverPhoto,
  resetCreateStore,
  removeCurrentEducation,
  isEmptyDataEducationWithFilters,
  setEducationFilterParams,
  setEducationDataWithNewPosition,
  clearEducationState,
  setCurrentEducation,
  setEducationEditMode,
  removeEducationCoverPhoto,
  updateEducationCoverPhoto,
  addEducationPhoto,
  addEducationFiles,
  updateEducationCoverPhotoWS,
  addShareUser,
  deleteEducationShareUser,
  updateShareUser,
  setSelectedUser,
  clearEducationFilters,
  setAssignPeopleLists,
} = educationSlice.actions;

export const educationReducer = educationSlice.reducer;
